import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  CountrySortBy,
  useGetCountriesQuery,
  useGetProductsQuery,
  useGetVirtualAssetsQuery,
} from "@generated/redux/services/generated/api";
import { Country, FilteredVirtualAsset, Product } from "@generated/types";

type GlobalDataContextValue = {
  products: Product[] | null;
  isLoadingProducts: boolean;
  isSuccessProducts: boolean;
  isErrorProducts: boolean;
  countries: Country[] | null;
  isLoadingCountries: boolean;
  isSuccessCountries: boolean;
  isErrorCountries: boolean;
  virtualAssets: FilteredVirtualAsset[] | null;
  isLoadingVirtualAssets: boolean;
  isSuccessVirtualAssets: boolean;
  isErrorVirtualAssets: boolean;
  /** Re-run the products fetch (used by the splash boot-error retry). */
  refetchProducts: () => Promise<unknown>;
  /** Re-run the countries fetch (used by the splash boot-error retry). */
  refetchCountries: () => Promise<unknown>;
};

const GlobalDataContext = createContext<GlobalDataContextValue>({
  products: null,
  isLoadingProducts: false,
  isSuccessProducts: false,
  isErrorProducts: false,
  countries: null,
  isLoadingCountries: false,
  isSuccessCountries: false,
  isErrorCountries: false,
  virtualAssets: null,
  isLoadingVirtualAssets: false,
  isSuccessVirtualAssets: false,
  isErrorVirtualAssets: false,
  refetchProducts: () => Promise.resolve(),
  refetchCountries: () => Promise.resolve(),
});

const GlobalDataProvider = ({ children }: WithChildren) => {
  // Defer reference-data queries to the next macrotask so the critical
  // /self request (fired by CustomerAccountProvider) gets sole access
  // to the browser's connection pool on initial pageload.
  const [deferred, setDeferred] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setDeferred(true), 0);
    return () => clearTimeout(id);
  }, []);

  const {
    data: productsData,
    isLoading: isRtkLoadingProducts,
    isError: isErrorProducts,
    isSuccess: isSuccessProducts,
    refetch: refetchProducts,
  } = useGetProductsQuery(undefined, {
    skip: !deferred,
    refetchOnMountOrArgChange: false,
  });

  const {
    data: countriesData,
    isLoading: isRtkLoadingCountries,
    isError: isErrorCountries,
    isSuccess: isSuccessCountries,
    refetch: refetchCountries,
  } = useGetCountriesQuery(
    { sortBy: CountrySortBy.Name },
    { skip: !deferred, refetchOnMountOrArgChange: false }
  );

  const {
    data: virtualAssetsData,
    isLoading: isRtkLoadingVirtualAssets,
    isError: isErrorVirtualAssets,
    isSuccess: isSuccessVirtualAssets,
  } = useGetVirtualAssetsQuery(
    {},
    { skip: !deferred, refetchOnMountOrArgChange: false }
  );

  // When skip is true, RTK Query reports isLoading: false even though
  // data isn't available yet. Override so consumers see isLoading: true
  // during the deferral period.
  const isLoadingProducts = !deferred || isRtkLoadingProducts;
  const isLoadingCountries = !deferred || isRtkLoadingCountries;
  const isLoadingVirtualAssets = !deferred || isRtkLoadingVirtualAssets;

  const products = productsData?.data || null;
  const countries = countriesData?.data || null;
  const virtualAssets = virtualAssetsData?.data || null;

  // Memoize so consumers that include the whole context in deps don't
  // re-render every time an unrelated parent re-renders.
  const value = useMemo<GlobalDataContextValue>(
    () => ({
      products,
      isLoadingProducts,
      isSuccessProducts,
      isErrorProducts,
      countries,
      isLoadingCountries,
      isSuccessCountries,
      isErrorCountries,
      virtualAssets,
      isLoadingVirtualAssets,
      isSuccessVirtualAssets,
      isErrorVirtualAssets,
      refetchProducts,
      refetchCountries,
    }),
    [
      products,
      isLoadingProducts,
      isSuccessProducts,
      isErrorProducts,
      countries,
      isLoadingCountries,
      isSuccessCountries,
      isErrorCountries,
      virtualAssets,
      isLoadingVirtualAssets,
      isSuccessVirtualAssets,
      isErrorVirtualAssets,
      refetchProducts,
      refetchCountries,
    ]
  );

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
};

export default GlobalDataProvider;

export const useGlobalData = () => useContext(GlobalDataContext);
